import { useCallback, useState } from "react";
import {
  User,
  Mail,
  Building2,
  Phone,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

const FormField = ({ label, icon: Icon, children }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      {children}
    </div>
  </div>
);

export default function PublisherForm({ form, isEditing, onChange, onSubmit, onCancel }) {
  const [showPassword, setShowPassword] = useState(false);

  const handleFieldChange = useCallback(
    (field) => (e) => {
      onChange((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [onChange]
  );

  const inputClass =
    "w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all";

  return (
    <form onSubmit={onSubmit} className="p-6 space-y-5">
      <p className="text-xs text-gray-400">* Duplicate emails are not allowed</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Full Name" icon={User}>
          <input
            type="text"
            placeholder="e.g. John Smith"
            className={inputClass}
            value={form.name || ""}
            onChange={handleFieldChange("name")}
            required
          />
        </FormField>

        <FormField label="Email Address" icon={Mail}>
          <input
            type="email"
            placeholder="e.g. john@example.com"
            className={inputClass}
            value={form.email || ""}
            onChange={handleFieldChange("email")}
            required
          />
        </FormField>

        <FormField label="Company" icon={Building2}>
          <input
            type="text"
            placeholder="e.g. Acme Corp"
            className={inputClass}
            value={form.company || ""}
            onChange={handleFieldChange("company")}
          />
        </FormField>

        <FormField label="Phone Number" icon={Phone}>
          <input
            type="tel"
            placeholder="e.g. +1 234 567 8900"
            className={inputClass}
            value={form.phone || ""}
            onChange={handleFieldChange("phone")}
          />
        </FormField>

        <FormField label="Account Status" icon={ShieldCheck}>
          <select
            className={inputClass}
            value={form.status || "Pending"}
            onChange={handleFieldChange("status")}
          >
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Suspended">Suspended</option>
          </select>
        </FormField>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Password {isEditing && <span className="text-gray-400 font-normal">(update if needed)</span>}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all font-mono"
              value={form.password || ""}
              onChange={handleFieldChange("password")}
              required={!isEditing}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded-xl hover:brightness-110 hover:shadow-lg transition-all"
        >
          {isEditing ? "Update Publisher" : "Add Publisher"}
        </button>
      </div>
    </form>
  );
}
